import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { Aircraft, Airline } from '@aviation/cabinapp-airlinedata-sdk';
import { toast } from 'react-toastify';
import { LookupItem, LookupKind } from './LookupDialog';
import LookupSelect from './LookupSelect';

export interface IAircraftEditDialogProps {
    open: boolean;
    aircraft: Aircraft;
    ifes: Array<LookupItem>;
    ovens: Array<LookupItem>;
    airlines: Array<Airline>;
    onSave: (item: Aircraft) => Promise<boolean>;
    onSaved: () => void;
    onManage: (kind: LookupKind) => void;
    onClose: () => void;
}

const AircraftEditDialog = (props: IAircraftEditDialogProps) => {
    const { open, aircraft, ifes, ovens, airlines, onSave, onSaved, onManage, onClose } = props;
    const [draft, setDraft] = useState<Aircraft>({ ...aircraft, Ife: aircraft.Ife ?? [], Ovens: aircraft.Ovens ?? [] });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        const success = await onSave(draft);
        setSaving(false);

        if(success) {
            toast.success('Changes to aircraft saved successfully.');
            onSaved();
            onClose();
        } else
            toast.error('An error occurred while saving aircraft');
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='md'>
            <DialogTitle>
                Aircraft {aircraft.AcRegDash ?? aircraft.AcReg}
                <Typography variant='body2' color='text.secondary'>
                    {[aircraft.FleetName, aircraft.AcVerCode, aircraft.ClientCode].filter(v => !!v).join(' · ')}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <LookupSelect label='Inflight Entertainment' variant='outlined' value={draft.Ife ?? []} options={ifes} airlines={airlines}
                        clientCode={aircraft.ClientCode} onManage={() => onManage('ife')} onChange={(v) => setDraft({ ...draft, Ife: v })} />
                    <LookupSelect label='Ovens' variant='outlined' value={draft.Ovens ?? []} options={ovens} airlines={airlines}
                        clientCode={aircraft.ClientCode} onManage={() => onManage('oven')} onChange={(v) => setDraft({ ...draft, Ovens: v })} />
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <FormControlLabel label='Sky Interior' control={<Switch checked={draft.SkyInterior ?? false} onChange={(e) => setDraft({ ...draft, SkyInterior: e.target.checked })} />} />
                        <FormControlLabel label='Connectivity' control={<Switch checked={draft.Connectivity ?? false} onChange={(e) => setDraft({ ...draft, Connectivity: e.target.checked })} />} />
                    </Box>
                    <TextField label='Remarks' multiline minRows={6} fullWidth value={draft.Remarks ?? ''} onChange={(e) => setDraft({ ...draft, Remarks: e.target.value })} />
                </Box>
            </DialogContent>
            <DialogActions>
                {saving && <CircularProgress size={19} />}
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant='contained' onClick={handleSave} disabled={saving}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AircraftEditDialog;
